import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { SearchInput } from '@/components/SearchInput';
import { TemplatePreview } from './TemplatePreview';
import {
  templateGalleryService,
  DefaultTemplate,
  GalleryListParams,
} from '@/services/template-gallery.service';
import { TemplateCategory } from '@/services/templates.service';
import toast from 'react-hot-toast';
import { Sparkles, Tag } from 'lucide-react';

interface Props {
  open: boolean;
  onClose: () => void;
}

const CATEGORIES: { label: string; value: TemplateCategory | '' }[] = [
  { label: 'All', value: '' },
  { label: 'Marketing', value: 'marketing' },
  { label: 'Utility', value: 'utility' },
  { label: 'Authentication', value: 'authentication' },
];

const CATEGORY_PILL: Record<TemplateCategory, string> = {
  marketing: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  utility: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  authentication: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
};

export const TemplateGalleryModal: React.FC<Props> = ({ open, onClose }) => {
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [category, setCategory] = useState<TemplateCategory | ''>('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<{ items: DefaultTemplate[]; total: number; page: number; limit: number } | null>(null);
  const [selected, setSelected] = useState<DefaultTemplate | null>(null);
  const [cloning, setCloning] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(
    (params: GalleryListParams) => {
      templateGalleryService.list(params).then((res) => {
        setData(res);
        if (!selected && res.items.length > 0) setSelected(res.items[0]);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      load({ q: q || undefined, category: category || undefined, page, limit: 20 });
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [open, q, category, page, load]);

  // Reset when closed
  useEffect(() => {
    if (!open) { setQ(''); setCategory(''); setPage(1); setData(null); setSelected(null); }
  }, [open]);

  const handleClone = async () => {
    if (!selected) return;
    setCloning(true);
    try {
      const cloned = await templateGalleryService.clone(selected.defaultTemplateUid);
      toast.success('Cloned to your drafts');
      onClose();
      nav(`/templates/${cloned.templateUid}`);
    } catch {
      // error toast handled by api interceptor
    } finally {
      setCloning(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Browse template gallery" widthClass="max-w-6xl">
      {/* Override inner padding for two-column layout */}
      <div className="-m-4">
        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-2.5 px-4 py-3 border-b border-border bg-surface-alt/50">
          <SearchInput
            value={q}
            onChange={(v) => { setQ(v); setPage(1); }}
            placeholder="Search templates…"
          />
          <div className="flex gap-1.5">
            {CATEGORIES.map((c) => (
              <button
                key={c.value}
                onClick={() => { setCategory(c.value); setPage(1); }}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  category === c.value
                    ? 'bg-gradient-to-r from-amber-400 to-amber-600 text-white shadow-sm'
                    : 'bg-surface border border-border text-text-secondary hover:border-primary hover:text-primary'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* Two-column body */}
        <div className="flex" style={{ minHeight: 520, maxHeight: 'calc(80vh - 120px)' }}>
          {/* Left: template list */}
          <div className="w-[380px] shrink-0 border-r border-border overflow-y-auto">
            {!data ? (
              /* Shimmer skeletons */
              <div className="p-3 space-y-2">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="rounded-xl border border-border bg-surface p-3 animate-pulse">
                    <div className="flex gap-3">
                      <div className="w-14 h-10 rounded-lg bg-surface-alt shrink-0" />
                      <div className="flex-1 space-y-2">
                        <div className="h-3 bg-surface-alt rounded w-3/4" />
                        <div className="h-2 bg-surface-alt rounded w-full" />
                        <div className="h-2 bg-surface-alt rounded w-5/6" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : data.items.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full py-12 text-center px-6">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 mb-4">
                  <Sparkles size={26} className="text-primary" />
                </div>
                <h4 className="font-semibold text-text-primary mb-1">No templates found</h4>
                <p className="text-xs text-text-muted">Try a different search or category.</p>
              </div>
            ) : (
              <>
                <div className="p-3 space-y-2">
                  {data.items.map((tpl) => (
                    <GalleryListItem
                      key={tpl.defaultTemplateUid}
                      tpl={tpl}
                      selected={selected?.defaultTemplateUid === tpl.defaultTemplateUid}
                      onClick={() => setSelected(tpl)}
                    />
                  ))}
                </div>
                {/* Mini pagination */}
                {data.total > data.limit && (
                  <div className="flex items-center justify-between px-3 py-2 border-t border-border text-xs text-text-muted">
                    <span>{data.total} templates</span>
                    <div className="flex gap-1.5">
                      <button
                        disabled={page <= 1}
                        onClick={() => setPage((p) => p - 1)}
                        className="px-2 py-1 rounded border border-border disabled:opacity-40 hover:bg-surface-alt"
                      >
                        ←
                      </button>
                      <button
                        disabled={page * data.limit >= data.total}
                        onClick={() => setPage((p) => p + 1)}
                        className="px-2 py-1 rounded border border-border disabled:opacity-40 hover:bg-surface-alt"
                      >
                        →
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Right: preview */}
          <div className="flex-1 overflow-y-auto">
            {!selected ? (
              <div className="flex flex-col items-center justify-center h-full text-center px-8">
                <Sparkles size={36} className="text-text-muted mb-3" />
                <p className="text-sm text-text-muted">Select a template on the left to preview it here.</p>
              </div>
            ) : (
              <SelectedPreview tpl={selected} cloning={cloning} onClone={handleClone} />
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

// ---------------------------------------------------------------------------

const GalleryListItem: React.FC<{
  tpl: DefaultTemplate;
  selected: boolean;
  onClick: () => void;
}> = ({ tpl, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full text-left rounded-xl border p-3 transition-all duration-150 group ${
      selected
        ? 'border-amber-400 bg-amber-50/60 dark:bg-amber-900/20 shadow-sm ring-1 ring-amber-400/40'
        : 'border-border bg-surface hover:border-primary/40 hover:bg-surface-alt hover:-translate-y-px hover:shadow-soft'
    }`}
  >
    <div className="flex gap-3 items-start">
      {tpl.thumbnailUrl ? (
        <img
          src={tpl.thumbnailUrl}
          alt={tpl.title}
          className="w-14 h-10 rounded-lg object-cover shrink-0 border border-border"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      ) : (
        <div className="w-14 h-10 rounded-lg bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 flex items-center justify-center shrink-0">
          <Sparkles size={16} className="text-amber-500" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-text-primary truncate">{tpl.title}</div>
        {tpl.description && (
          <div className="text-xs text-text-muted line-clamp-2 mt-0.5">{tpl.description}</div>
        )}
        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold capitalize ${CATEGORY_PILL[tpl.category]}`}>
            {tpl.category}
          </span>
          {tpl.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] bg-surface-alt border border-border text-text-muted">
              <Tag size={8} />{tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  </button>
);

// ---------------------------------------------------------------------------

const SelectedPreview: React.FC<{
  tpl: DefaultTemplate;
  cloning: boolean;
  onClone: () => void;
}> = ({ tpl, cloning, onClone }) => (
  <div className="flex flex-col h-full">
    {/* Meta header */}
    <div className="px-6 pt-5 pb-4 border-b border-border">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold text-text-primary leading-tight">{tpl.title}</h3>
          {tpl.description && (
            <p className="text-sm text-text-muted mt-1 leading-relaxed max-w-prose">{tpl.description}</p>
          )}
          <div className="flex items-center gap-2 mt-2.5 flex-wrap">
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${CATEGORY_PILL[tpl.category]}`}>
              {tpl.category}
            </span>
            <span className="text-xs text-text-muted">{tpl.language.toUpperCase()}</span>
            {tpl.tags.map((tag) => (
              <span key={tag} className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-surface-alt border border-border text-text-muted">
                <Tag size={10} />{tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>

    {/* Scrollable preview area */}
    <div className="flex-1 overflow-y-auto px-6 py-5 flex justify-center">
      <TemplatePreview
        headerType={tpl.headerType ?? 'NONE'}
        headerText={tpl.headerText}
        headerImageUrl={tpl.headerImageUrl}
        headerVideoUrl={tpl.headerVideoUrl}
        headerDocumentUrl={tpl.headerDocumentUrl}
        headerDocumentFilename={tpl.headerDocumentFilename}
        headerLocation={tpl.headerLocation}
        body={tpl.body}
        footer={tpl.footer}
        buttons={tpl.buttons}
        samples={tpl.previewSamples}
        contactName={tpl.title}
      />
    </div>

    {/* CTA */}
    <div className="px-6 pb-5 pt-3 border-t border-border flex items-center justify-between gap-3 bg-surface">
      <p className="text-xs text-text-muted">
        This will create an editable copy in your drafts.
      </p>
      <Button
        size="lg"
        loading={cloning}
        onClick={onClone}
        iconLeft={<Sparkles size={16} />}
        className="shrink-0"
      >
        Use this template
      </Button>
    </div>
  </div>
);
